import { useEffect, useRef } from 'react';

import { MenuList, Paper, Popper } from '@mui/material';
import { MenuItemType } from 'common/components/Menu';
import { MenuItem } from 'common/components/Menu/components';

const MENU_ITEM_HEIGHT = 36;

export const TimePickerSelect = ({
  anchorEl,
  options,
  activeValueIndex,
  scrollToIndex,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  options: MenuItemType[];
  activeValueIndex: number | undefined;
  scrollToIndex?: number;
  onClose(): void;
}) => {
  const paperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorEl) return;

    // Scroll to the active value if one exists, otherwise scroll to the default position
    const targetIndex = activeValueIndex ?? scrollToIndex;
    if (targetIndex === undefined) return;

    // Use requestAnimationFrame to ensure the DOM has updated before scrolling
    const frameId = requestAnimationFrame(() => {
      if (paperRef.current) {
        paperRef.current.scrollTop = targetIndex * MENU_ITEM_HEIGHT;
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [anchorEl, activeValueIndex, scrollToIndex]);

  return (
    <Popper anchorEl={anchorEl} open={Boolean(anchorEl)} placement="bottom-start" disablePortal={false}>
      <Paper
        ref={paperRef}
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: 210,
        }}
      >
        <MenuList data-testid="TimePickerSelect-menu" variant="selectedMenu">
          {options.map((item, index) => (
            <MenuItem key={index} isActive={index === activeValueIndex} item={item} onClose={onClose} />
          ))}
        </MenuList>
      </Paper>
    </Popper>
  );
};
